import type { paths } from '@ccp/api-client';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, expectBody } from '../../api/client.js';

/**
 * The agent's own reads and writes, as the API answers them.
 *
 * The types come from the OpenAPI document, so a field a screen reads is a field the API returns.
 *
 * Every control is a `POST` that answers **202**: the platform has recorded what the agent asked
 * for, and the audio moves when the channel adapter acts on it. So none of these mutations waits for
 * the call to change — what changes the screen is the event that follows, through the socket. A
 * mutation that optimistically redrew the call would be guessing at the one thing the adapter owns.
 */
type Json<T> = T extends { content: { 'application/json': infer B } } ? B : never;
type Body<T> = T extends { requestBody: { content: { 'application/json': infer B } } } ? B : never;

export type InteractionDetail = Json<paths['/v1/interactions/{id}']['get']['responses'][200]>;
export type MyWork = Json<paths['/v1/interactions/mine']['get']['responses'][200]>['items'];
export type InteractionSummary = Json<
  paths['/v1/interactions']['get']['responses'][200]
>['items'][number];
export type TimelineResponse = Json<
  paths['/v1/interactions/conversations/{id}/timeline']['get']['responses'][200]
>;
export type TransferInput = Body<paths['/v1/interactions/{id}/transfer']['post']>;
export type DispositionInput = Body<paths['/v1/interactions/{id}/disposition']['post']>;

export const interactionKeys = {
  all: ['interactions'] as const,
  mine: ['interactions', 'mine'] as const,
  one: (id: string) => ['interactions', id] as const,
  timeline: (conversationId: string) => ['interactions', 'timeline', conversationId] as const,
  ofContact: (contactId: string) => ['interactions', 'contact', contactId] as const,
};

/**
 * What this agent has in front of them, from the platform rather than from the tab's memory.
 *
 * The workspace learns about an offer from the socket and remembers only what it was told, so after
 * a reload — or in a second window, or after the browser was killed mid-call — nothing local knows
 * which call is being held or which disposition is still owed. This does, because the server does.
 *
 * It is not polled. Every event about an interaction invalidates `['interactions', <id>]` and the
 * events that start and end the agent's own work invalidate this key with it, so the screen follows
 * the socket and falls back on a refetch only when the socket has been away.
 */
export function useMyWork() {
  return useQuery({
    queryKey: interactionKeys.mine,
    queryFn: async () => expectBody(await api.GET('/v1/interactions/mine')).items,
  });
}

/** What this contact has done before, for the panel beside the call. */
export function useContactInteractions(contactId: string | undefined, limit = 10) {
  return useQuery({
    queryKey: interactionKeys.ofContact(contactId ?? 'none'),
    enabled: contactId !== undefined,
    queryFn: async () =>
      expectBody(
        await api.GET('/v1/interactions', {
          params: { query: { contactId: contactId as string, limit } },
        }),
      ).items,
  });
}

/**
 * The interaction behind an offer.
 *
 * This is the screen pop: the socket says *that* interaction X was offered, and this is what turns
 * that into a name, a number and a queue. It is fetched the moment the offer arrives rather than
 * when the agent accepts, because the whole point of a screen pop is that the context is there
 * before they say hello.
 */
export function useInteraction(interactionId: string | undefined) {
  return useQuery({
    queryKey: interactionKeys.one(interactionId ?? 'none'),
    enabled: interactionId !== undefined,
    queryFn: async () =>
      expectBody(
        await api.GET('/v1/interactions/{id}', {
          params: { path: { id: interactionId as string } },
        }),
      ),
  });
}

export function useTimeline(conversationId: string | undefined) {
  return useQuery({
    queryKey: interactionKeys.timeline(conversationId ?? 'none'),
    enabled: conversationId !== undefined,
    queryFn: async () =>
      expectBody(
        await api.GET('/v1/interactions/conversations/{id}/timeline', {
          params: { path: { id: conversationId as string } },
        }),
      ).items,
  });
}

/**
 * Answering an offer.
 *
 * Accepting is idempotent by offer in the command, so a double click is one assignment rather than a
 * refusal — which matters here more than anywhere, because the button is pressed under time pressure
 * on a laptop that may have just woken up.
 */
export function useAcceptOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (offerId: string) =>
      expectBody(
        await api.POST('/v1/interactions/offers/{id}/accept', {
          params: { path: { id: offerId } },
        }),
      ),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: interactionKeys.all }),
  });
}

export function useRejectOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (offerId: string) =>
      expectBody(
        await api.POST('/v1/interactions/offers/{id}/reject', {
          params: { path: { id: offerId } },
        }),
      ),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: interactionKeys.all }),
  });
}

/** Hold and resume, which are the same shape. */
function useControl(path: '/v1/interactions/{id}/hold' | '/v1/interactions/{id}/resume') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (interactionId: string) =>
      expectBody(await api.POST(path, { params: { path: { id: interactionId } } })),
    onSettled: (_data, _error, interactionId) =>
      void queryClient.invalidateQueries({ queryKey: interactionKeys.one(interactionId) }),
  });
}

export const useHold = () => useControl('/v1/interactions/{id}/hold');
export const useResume = () => useControl('/v1/interactions/{id}/resume');

export function useSendDtmf() {
  return useMutation({
    mutationFn: async (input: { readonly interactionId: string; readonly digits: string }) =>
      expectBody(
        await api.POST('/v1/interactions/{id}/dtmf', {
          params: { path: { id: input.interactionId } },
          body: { digits: input.digits },
        }),
      ),
  });
}

/**
 * **Placing a call**, which is the one request on this screen that spends the tenant's money.
 *
 * Everything the platform needs beyond the number is the platform's: who is calling comes from the
 * token, which phone rings first is the agent's own ready device, which node dials is the
 * deployment's and which trunk it leaves by is the destination policy's. So the body is a
 * destination and, when the screen knows it, the contact being called — a click on a contact's
 * number knows which of two people share it, and a typed number does not.
 *
 * It answers `202`: the platform has written the call down and will dial it. The work list is
 * invalidated because a placed call **is** the agent's work — it assigns them and takes a unit of
 * their capacity — which is also what makes the browser answer the leg the platform rings it on.
 */
export function usePlaceCall() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { readonly destination: string; readonly contactId?: string }) =>
      expectBody(await api.POST('/v1/interactions/calls', { body: input })),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: interactionKeys.all }),
  });
}

export function useTransfer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { readonly interactionId: string } & TransferInput) => {
      const { interactionId, ...body } = input;
      return expectBody(
        await api.POST('/v1/interactions/{id}/transfer', {
          params: { path: { id: interactionId } },
          body,
        }),
      );
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: interactionKeys.all }),
  });
}

export function useHangUp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { readonly interactionId: string; readonly reason?: string }) =>
      expectBody(
        await api.POST('/v1/interactions/{id}/hangup', {
          params: { path: { id: input.interactionId } },
          body: input.reason === undefined ? {} : { reason: input.reason },
        }),
      ),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: interactionKeys.all }),
  });
}

/** The disposition, which is also what ends the wrap-up. */
export function useRecordDisposition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { readonly interactionId: string } & DispositionInput) => {
      const { interactionId, ...body } = input;
      return expectBody(
        await api.POST('/v1/interactions/{id}/disposition', {
          params: { path: { id: interactionId } },
          body,
        }),
      );
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: interactionKeys.all }),
  });
}

/**
 * The screen pop, fetched on the offer rather than on the acceptance.
 *
 * LB-4 is the budget for context being on screen, and the way to meet it is not to make the fetch
 * faster but to start it earlier: the socket says interaction X was offered, and this asks for the
 * interaction and the contact behind it while the phone is still ringing. By the time the agent has
 * pressed accept the two reads are in the cache, and the panel renders from it.
 *
 * `ensureQueryData` and not `fetchQuery`: an offer re-announced after a reconnection must not start
 * the same two requests again.
 */
export function prefetchScreenPop(queryClient: QueryClient, interactionId: string): void {
  void queryClient
    .ensureQueryData({
      queryKey: interactionKeys.one(interactionId),
      queryFn: async () =>
        expectBody(
          await api.GET('/v1/interactions/{id}', { params: { path: { id: interactionId } } }),
        ),
    })
    .then((detail) => {
      const contactId = detail.interaction.contactId;
      void queryClient.ensureQueryData({
        queryKey: ['contacts', contactId],
        queryFn: async () =>
          expectBody(await api.GET('/v1/contacts/{id}', { params: { path: { id: contactId } } })),
      });
    })
    // A failed prefetch is not an error anybody can act on: the panel will ask again when it
    // mounts, and the offer is answerable either way.
    .catch(() => undefined);
}
